/**
 * Material Cascade Service
 *
 * Centralizes all material cascade business logic with caching for performance.
 * Implements Excel VLOOKUP behavior and material-geogrid dependencies.
 *
 * This service extracts the complex business logic from useFormMaterialCascade
 * to improve testability, maintainability, and performance through intelligent caching.
 */

import {
    getBaseMaterialDependentFields,
    getSubbaseMaterialDependentFields,
    getSurfaceMaterialDependentFields,
    calculateBaseMaterialCascadeEffects,
    calculateSubbaseMaterialCascadeEffects,
} from './ProjectTypeService';
import { MaterialDatabaseService } from './MaterialDatabaseService';
import ConfigurationService from './ConfigurationService';
import { logDebug } from '../utils/utils';
import { LogTag } from '../config/loggingConfig';

/**
 * Format numbers to maximum 2 decimal places
 * @param value - The number to format
 * @returns Number with maximum 2 decimal places
 */
const formatToTwoDecimals = (value: number | undefined | null): number | undefined | null => {
    if (value === undefined || value === null) return value;
    return Math.round(value * 100) / 100;
};

// Types for cascade effects
export interface BaseCascadeEffects {
    baseProperties: ReturnType<typeof getBaseMaterialDependentFields>;
    unstabilizedCoefficient: number;
    stabilizedCoefficient: number;
    designCBR: number;
    baseModulus: number;
    lcrValue: number;
    geogridEnabled: boolean;
}

export interface SubbaseCascadeEffects {
    subbaseProperties: ReturnType<typeof getSubbaseMaterialDependentFields>;
    unstabilizedCoefficient: number;
    stabilizedCoefficient: number;
    designCBR: number;
    subbaseModulus: number;
    lcrValue: number;
    geogridEnabled: boolean;
}

export interface SurfaceCascadeEffects {
    surfaceProperties: ReturnType<typeof getSurfaceMaterialDependentFields>;
    layerCoefficient: number;
}

// Cache key type for type safety
type CacheKey = string;

/**
 * Centralized service for material cascade operations with intelligent caching
 */
export class MaterialCascadeService {
    private cache: Map<CacheKey, any> = new Map();
    private configService: ConfigurationService;
    private materialDatabaseService: MaterialDatabaseService;

    // Cache TTL in milliseconds (5 minutes)
    private readonly CACHE_TTL = 5 * 60 * 1000;
    private cacheTimestamps: Map<CacheKey, number> = new Map();

    constructor() {
        this.configService = ConfigurationService.getInstance();
        this.materialDatabaseService = new MaterialDatabaseService();
    }

    /**
     * Calculate base material cascade effects with caching
     * @param materialId - Base material ID
     * @param geogridId - Optional geogrid ID
     * @returns Complete cascade effects for base material
     */
    calculateBaseCascadeEffects(materialId: string, geogridId?: string | null): BaseCascadeEffects {
        const cacheKey = `base_${materialId}_${geogridId || 'none'}`;

        // Check cache first
        const cached = this.getCachedResult<BaseCascadeEffects>(cacheKey);
        if (cached) {
            return cached;
        }

        // Calculate effects using existing service functions
        const effects = calculateBaseMaterialCascadeEffects(materialId, geogridId || null);

        // Cache the result
        this.setCachedResult(cacheKey, effects);

        return effects;
    }

    /**
     * Calculate subbase material cascade effects with caching
     * @param materialId - Subbase material ID
     * @param geogridId - Optional geogrid ID
     * @returns Complete cascade effects for subbase material
     */
    calculateSubbaseCascadeEffects(materialId: string, geogridId?: string | null): SubbaseCascadeEffects {
        const cacheKey = `subbase_${materialId}_${geogridId || 'none'}`;

        const cached = this.getCachedResult<SubbaseCascadeEffects>(cacheKey);
        if (cached) {
            return cached;
        }

        const effects = calculateSubbaseMaterialCascadeEffects(materialId, geogridId || null);
        this.setCachedResult(cacheKey, effects);

        return effects;
    }

    /**
     * Calculate surface material cascade effects with caching
     * @param materialId - Surface material ID
     * @returns Surface cascade effects
     */
    calculateSurfaceCascadeEffects(materialId: string): SurfaceCascadeEffects {
        const cacheKey = `surface_${materialId}`;

        const cached = this.getCachedResult<SurfaceCascadeEffects>(cacheKey);
        if (cached) {
            logDebug('MaterialCascadeService', `🔄 SURFACE CASCADE: Using cached result for ${materialId}`, cached, LogTag.CASCADE_DEBUG);
            return cached;
        }

        const surfaceProperties = getSurfaceMaterialDependentFields(materialId);
        const effects: SurfaceCascadeEffects = {
            surfaceProperties,
            layerCoefficient: surfaceProperties.layerCoefficienta1,
        };

        logDebug('MaterialCascadeService', `🔄 SURFACE CASCADE: Calculated effects for ${materialId}`, { materialId, effects }, LogTag.CASCADE_DEBUG);

        this.setCachedResult(cacheKey, effects);
        return effects;
    }

    /**
     * Get auto-selected geogrid for a material
     * @param layer - Layer type ('base' | 'subbase')
     * @param materialId - Material ID
     * @returns Auto-selected geogrid ID or 'None'
     */
    getAutoSelectedGeogrid(layer: 'base' | 'subbase', materialId: string): string {
        const cacheKey = `auto_geogrid_${layer}_${materialId}`;

        const cached = this.getCachedResult<string>(cacheKey);
        if (cached !== undefined) {
            return cached;
        }

        const result = this.configService.getAutoSelectedGeogrid(layer, materialId);
        this.setCachedResult(cacheKey, result);

        return result;
    }

    /**
     * Validate geogrid compatibility with material
     * @param layer - Layer type ('base' | 'subbase')
     * @param materialId - Material ID
     * @param geogridId - Geogrid ID to validate
     * @returns True if compatible, false otherwise
     */
    validateGeogridCompatibility(layer: 'base' | 'subbase', materialId: string, geogridId?: string): boolean {
        if (!geogridId) return true; // No geogrid is always valid

        const cacheKey = `compat_${layer}_${materialId}_${geogridId}`;

        const cached = this.getCachedResult<boolean>(cacheKey);
        if (cached !== undefined) {
            return cached;
        }

        let isValid: boolean;
        if (layer === 'base') {
            isValid = this.materialDatabaseService.isBaseGeogridSelectionEnabled(materialId);
        } else {
            isValid = this.materialDatabaseService.isSubbaseGeogridSelectionEnabled(materialId);
        }

        this.setCachedResult(cacheKey, isValid);
        return isValid;
    }

    /**
     * Calculate complete material cascade update for form integration
     * @param layer - Layer type
     * @param materialId - Material ID
     * @param geogridId - Optional geogrid ID
     * @returns Object with field updates for form integration
     */
    calculateMaterialCascadeUpdate(layer: 'base' | 'subbase' | 'surface', materialId: string, geogridId?: string | null): Record<string, any> {
        logDebug('MaterialCascadeService', `🔄 MATERIAL CASCADE: Starting update for ${layer} with material ${materialId}`, { layer, materialId, geogridId }, LogTag.CASCADE_DEBUG);

        switch (layer) {
            case 'base':
                const baseEffects = this.calculateBaseCascadeEffects(materialId, geogridId);
                const autoBaseGeogrid = this.getAutoSelectedGeogrid('base', materialId);

                const baseUpdate = {
                    [`${layer}Course.${layer}Material`]: materialId,
                    [`${layer}Course.layerCoefficienta2`]: formatToTwoDecimals(baseEffects.baseProperties.layerCoefficienta2),
                    [`${layer}Course.effectivePorosity`]: formatToTwoDecimals(baseEffects.baseProperties.effectivePorosity),
                    [`${layer}Course.permeabilityCoefficient`]: formatToTwoDecimals(baseEffects.baseProperties.permeabilityCoefficient),
                    [`${layer}Course.cbrBaseCourse`]: formatToTwoDecimals(baseEffects.baseProperties.cbrBaseCourse),
                    [`${layer}Course.ev2BaseCourse`]: formatToTwoDecimals(baseEffects.baseProperties.ev2BaseCourse),
                    ...(autoBaseGeogrid !== 'None' && { [`${layer}Course.${layer}Geogrid`]: autoBaseGeogrid }),
                };
                logDebug('MaterialCascadeService', `🔄 BASE CASCADE: Calculated update`, baseUpdate, LogTag.CASCADE_DEBUG);
                return baseUpdate;

            case 'subbase':
                const subbaseEffects = this.calculateSubbaseCascadeEffects(materialId, geogridId);
                const autoSubbaseGeogrid = this.getAutoSelectedGeogrid('subbase', materialId);

                const subbaseUpdate = {
                    [`${layer}Course.${layer}Material`]: materialId,
                    [`${layer}Course.layerCoefficienta3`]: formatToTwoDecimals(subbaseEffects.subbaseProperties.layerCoefficienta3),
                    [`${layer}Course.effectivePorosity`]: formatToTwoDecimals(subbaseEffects.subbaseProperties.effectivePorosity),
                    [`${layer}Course.permeabilityCoefficient`]: formatToTwoDecimals(subbaseEffects.subbaseProperties.permeabilityCoefficient),
                    [`${layer}Course.cbrSubbaseCourse`]: formatToTwoDecimals(subbaseEffects.subbaseProperties.cbrSubbaseCourse),
                    [`${layer}Course.ev2SubbaseCourse`]: formatToTwoDecimals(subbaseEffects.subbaseProperties.ev2SubbaseCourse),
                    ...(autoSubbaseGeogrid !== 'None' && { [`${layer}Course.${layer}Geogrid`]: autoSubbaseGeogrid }),
                };
                logDebug('MaterialCascadeService', `🔄 SUBBASE CASCADE: Calculated update`, subbaseUpdate, LogTag.CASCADE_DEBUG);
                return subbaseUpdate;

            case 'surface':
                const surfaceEffects = this.calculateSurfaceCascadeEffects(materialId);

                const surfaceUpdate = {
                    [`${layer}.${layer}Material`]: materialId,
                    [`${layer}.layerCoefficienta1`]: formatToTwoDecimals(surfaceEffects.layerCoefficient),
                    [`${layer}.minThickness`]: formatToTwoDecimals(surfaceEffects.surfaceProperties.minThickness),
                };
                logDebug('MaterialCascadeService', `🔄 SURFACE CASCADE: Calculated update`, surfaceUpdate, LogTag.CASCADE_DEBUG);
                return surfaceUpdate;

            default:
                logDebug('MaterialCascadeService', `❌ CASCADE: Unknown layer type ${layer}`, { layer, materialId }, LogTag.CASCADE_DEBUG);
                return {};
        }
    }

    /**
     * Clear all cached results (useful for testing or memory management)
     */
    clearCache(): void {
        this.cache.clear();
        this.cacheTimestamps.clear();
    }

    /**
     * Clear expired cache entries
     */
    cleanupExpiredCache(): void {
        const now = Date.now();
        const expiredKeys: CacheKey[] = [];

        this.cacheTimestamps.forEach((timestamp, key) => {
            if (now - timestamp > this.CACHE_TTL) {
                expiredKeys.push(key);
            }
        });

        expiredKeys.forEach(key => {
            this.cache.delete(key);
            this.cacheTimestamps.delete(key);
        });
    }

    /**
     * Get cached result with TTL check
     * @private
     */
    private getCachedResult<T>(key: CacheKey): T | undefined {
        const timestamp = this.cacheTimestamps.get(key);
        if (!timestamp) return undefined;

        // Check if expired
        if (Date.now() - timestamp > this.CACHE_TTL) {
            this.cache.delete(key);
            this.cacheTimestamps.delete(key);
            return undefined;
        }

        return this.cache.get(key) as T;
    }

    /**
     * Set cached result with timestamp
     * @private
     */
    private setCachedResult<T>(key: CacheKey, value: T): void {
        this.cache.set(key, value);
        this.cacheTimestamps.set(key, Date.now());
    }

    /**
     * Get cache statistics for monitoring
     */
    getCacheStats(): { size: number; hitRate?: number } {
        return {
            size: this.cache.size,
            // Hit rate calculation would require additional tracking
        };
    }
}

// Export singleton instance
export const materialCascadeService = new MaterialCascadeService();
export default materialCascadeService;
